/**
 * imageResize.utils.ts
 *
 * Client-side image resizing using the Canvas API.
 * Compresses images to a maximum width/height of 1920px at JPEG 85% quality
 * before uploading to S3. This keeps storage lean and improves load times.
 */

const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.85;

/**
 * Resize a File to at most MAX_DIMENSION × MAX_DIMENSION, preserving aspect ratio.
 * Returns a new File (JPEG) ready for S3 upload.
 *
 * If the image is already within the limits it is returned as-is (no quality loss).
 */
export async function resizeImageFile(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const { width, height } = img;

      // No resize needed — return original file
      if (width <= MAX_DIMENSION && height <= MAX_DIMENSION) {
        resolve(file);
        return;
      }

      // Calculate new dimensions maintaining aspect ratio
      const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
      const newWidth = Math.round(width * ratio);
      const newHeight = Math.round(height * ratio);

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file); // Canvas not supported — fall back to original
        return;
      }

      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob(
        blob => {
          if (!blob) {
            resolve(file); // Blob conversion failed — fall back to original
            return;
          }

          // Keep original filename but force .jpg extension
          const baseName = file.name.replace(/\.[^.]+$/, "");
          const resizedFile = new File([blob], `${baseName}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });

          resolve(resizedFile);
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image: ${file.name}`));
    };

    img.src = objectUrl;
  });
}
